import { useEffect, useMemo } from 'react';

import { AttributeOptions, ServiceTypeOptions } from './types';
import { CustomNodeAttributeType } from '../../../@types/service.type';
import { ServiceTypeMappingType } from '../../../@types/structure.type';
import {
  FaultCategory,
  NodeAttributeType,
  PropertyType,
  ServiceTypeCode,
} from '../../../configs/enums';
import { useGetServiceActionProcessTypes } from '../../../queries/service-query';
import {
  useGetFilteredNodeAttributesByCustomRange,
  useGetNodeAttributesByCustomRange,
} from '../../../queries/structure-query';
import {
  actionTypes,
  getInitialPriority,
  priorityTypes,
  processTypes,
  reportedModelTypes,
  serviceTypes,
} from '../../../utils/services';
import { buildFaultGroups } from '../../Part/CreateRepairJob/useRepairJobAttributes';

const MODEL_ATTRS = [NodeAttributeType.Models];

const MAPPING_ATTRS = [NodeAttributeType.ServiceTypeMappings];

const PRIORITY_ATTRS = [NodeAttributeType.PriorityAndServices];

const FAULT_ATTRS = [NodeAttributeType.FaultCategory, NodeAttributeType.FaultCode];

export interface UseCreateServiceOptionsArgs {
  parentId: string;
  clientId: number;
  // Contract is the preferred scope for mappings + models (skips brand
  // intentionally — see useCreateServiceOptions for the fallback shape). 0
  // when no contract is in the picked location's hierarchy.
  contractId: number;
  serviceTypeCode: string;
  actionTypeCode: string;
}

export interface UseCreateServiceOptionsResult {
  serviceTypeOptions: ServiceTypeOptions;
  attributeOptions: AttributeOptions;
  // Raw model attributes (with the partType[] array on each). Used by the
  // client/customer create flow to derive the matching Reported Model from a
  // chosen Part Type — see resolveModelForPartType().
  rawModelAttributes: CustomNodeAttributeType[];
  mappingsLoading: boolean;
  priorityAttributesLoading: boolean;
}

const hasActivePriorities = (data: CustomNodeAttributeType[]): boolean =>
  data.some((a) => a.propertyTypeCode === PropertyType.PriorityAndService && a.value?.isActive);

// Mappings + models intentionally skip brand and ask the contract first
// (clients configure them at the contract level), then fall back to the
// client when the contract returns nothing. If no contract is in the
// hierarchy, the primary call goes straight to client and no fallback
// fires. The empty-string nodeId pseudo-disables the fallback hook (via
// useGetNodeAttributesByCustomRange's existing empty-string guard) until
// the primary actually resolves with no rows.
const useContractAttributeWithClientFallback = (
  contractNodeId: string,
  clientNodeId: string,
  attrs: NodeAttributeType[]
): { data: CustomNodeAttributeType[]; isFetching: boolean } => {
  const primaryNodeId = contractNodeId || clientNodeId;
  const fallbackEligible =
    clientNodeId !== '' && contractNodeId !== '' && contractNodeId !== clientNodeId;

  const primaryQuery = useGetNodeAttributesByCustomRange(primaryNodeId, attrs);
  const primary = primaryQuery.data as CustomNodeAttributeType[] | undefined;
  const primaryEmpty =
    !primaryQuery.isFetching && !!primaryQuery.dataUpdatedAt && (primary?.length ?? 0) === 0;

  const fallbackQuery = useGetNodeAttributesByCustomRange(
    fallbackEligible && primaryEmpty ? clientNodeId : '',
    attrs
  );

  const data = useMemo<CustomNodeAttributeType[]>(() => {
    const p = primary ?? [];
    if (p.length > 0) return p;
    if (!fallbackEligible) return p;
    return (fallbackQuery.data as CustomNodeAttributeType[] | undefined) ?? p;
  }, [primary, fallbackQuery.data, fallbackEligible]);

  return { data, isFetching: primaryQuery.isFetching || fallbackQuery.isFetching };
};

export const useCreateServiceOptions = ({
  parentId,
  clientId,
  contractId,
  serviceTypeCode,
  actionTypeCode,
}: UseCreateServiceOptionsArgs): UseCreateServiceOptionsResult => {
  const commonTypesQuery = useGetServiceActionProcessTypes(true);

  const contractNodeId = contractId > 0 ? String(contractId) : '';
  const clientNodeId = clientId > 0 ? String(clientId) : '';
  const mappingsResult = useContractAttributeWithClientFallback(
    contractNodeId,
    clientNodeId,
    MAPPING_ATTRS
  );
  const modelResult = useContractAttributeWithClientFallback(
    contractNodeId,
    clientNodeId,
    MODEL_ATTRS
  );

  // Priorities are scoped to the service type only — refetch whenever it changes,
  // independently of action type, which doesn't influence priority availability.
  // faultType is included to match the BFF's expected call shape; it doesn't
  // narrow priorities but the endpoint requires it to return any rows.
  const priorityQuery = useGetFilteredNodeAttributesByCustomRange(
    parentId,
    PRIORITY_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported
  );

  // Mirror legacy CMS2 behaviour: when the brand/contract-scoped node has no
  // active priorities for the chosen service type, fall back to whatever is
  // configured at the client level. Only mounted when a more-specific level
  // was actually selected (parentId !== clientId).
  const clientIdString = String(clientId);
  const fallbackEnabled = clientIdString !== '0' && clientIdString !== parentId;
  const clientPriorityQuery = useGetFilteredNodeAttributesByCustomRange(
    clientIdString,
    PRIORITY_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported
  );

  const faultQuery = useGetFilteredNodeAttributesByCustomRange(
    parentId,
    FAULT_ATTRS,
    serviceTypeCode,
    FaultCategory.Reported,
    actionTypeCode
  );

  useEffect(() => {
    if (parentId && serviceTypeCode) {
      priorityQuery.refetch();
    }
  }, [parentId, serviceTypeCode]);

  useEffect(() => {
    if (parentId && serviceTypeCode && actionTypeCode) {
      faultQuery.refetch();
    }
  }, [parentId, serviceTypeCode, actionTypeCode]);

  // Trigger the client-level fallback once the primary query resolves with no
  // active priorities. Using isFetched (not data presence) so we don't fire
  // before the primary call has actually returned.
  const primaryPriorityData = priorityQuery.data as CustomNodeAttributeType[] | undefined;
  const primaryHasPriorities = primaryPriorityData
    ? hasActivePriorities(primaryPriorityData)
    : false;
  const primaryFetched = priorityQuery.isFetched;

  useEffect(() => {
    if (
      fallbackEnabled &&
      serviceTypeCode &&
      primaryFetched &&
      !priorityQuery.isFetching &&
      !primaryHasPriorities
    ) {
      clientPriorityQuery.refetch();
    }
  }, [
    fallbackEnabled,
    serviceTypeCode,
    primaryFetched,
    primaryHasPriorities,
    priorityQuery.isFetching,
  ]);

  // Repair jobs are created from the dedicated /parts repair-job page, so they
  // must not be reachable from /services/create. Drop Repair from both surfaces
  // here: the override "Service Type" dropdown and any contract/client mapping
  // whose underlying serviceTypeCode resolves to Repair.
  const mappings = useMemo(
    () =>
      mappingsResult.data.filter((m) => {
        const v = m.value as ServiceTypeMappingType | undefined;
        return v?.selectedServiceType !== ServiceTypeCode.Repair;
      }),
    [mappingsResult.data]
  );
  const modelAttrs = modelResult.data;

  const priorityAttrs: CustomNodeAttributeType[] = useMemo(() => {
    const primary = primaryPriorityData ?? [];
    if (hasActivePriorities(primary)) return primary;
    if (!fallbackEnabled) return primary;
    return (clientPriorityQuery.data as CustomNodeAttributeType[] | undefined) ?? primary;
  }, [primaryPriorityData, clientPriorityQuery.data, fallbackEnabled]);

  const faultGroup = useMemo(() => buildFaultGroups(faultQuery.data ?? []), [faultQuery.data]);

  const serviceTypeOptions: ServiceTypeOptions = useMemo(() => {
    const rawServiceTypes = commonTypesQuery.data?.serviceTypes ?? [];
    const nonRepairServiceTypes = rawServiceTypes.filter((t) => t.code !== ServiceTypeCode.Repair);
    return {
      mappings,
      serviceTypes: serviceTypes(nonRepairServiceTypes) ?? [],
      actionTypes: actionTypes(commonTypesQuery.data?.actionTypes ?? []) ?? [],
      processTypes: processTypes(commonTypesQuery.data?.processTypes ?? []) ?? [],
    };
  }, [mappings, commonTypesQuery.data]);

  const attributeOptions: AttributeOptions = useMemo(
    () => ({
      priorities: priorityTypes(priorityAttrs) ?? [],
      // getInitialPriority operates on the raw attribute shape (filters by
      // propertyTypeCode + isActive); it returns the lone active priority's id
      // when only one exists, otherwise ''.
      initialPriorityCode: getInitialPriority(priorityAttrs),
      modelOptions: reportedModelTypes(modelAttrs) ?? [],
      mainFaultOptions: faultGroup.mainFaultOptions,
      descriptionsByFault: faultGroup.descriptionsByFault,
      faultHasDescriptions: faultGroup.faultHasDescriptions,
      faultsLoading: faultQuery.isFetching,
      hasNoFaults: !faultQuery.isFetching && faultGroup.mainFaultOptions.length === 0,
    }),
    [priorityAttrs, modelAttrs, faultGroup, faultQuery.isFetching]
  );

  return {
    serviceTypeOptions,
    attributeOptions,
    rawModelAttributes: modelAttrs,
    mappingsLoading: mappingsResult.isFetching,
    priorityAttributesLoading: priorityQuery.isFetching || clientPriorityQuery.isFetching,
  };
};

export { MODEL_ATTRS, MAPPING_ATTRS, PRIORITY_ATTRS, FAULT_ATTRS };
